import React, { useEffect, useState } from 'react';

import { useAppContext } from '@prisjakt/ui-app-context';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';

import { Direction } from '@prisjakt/ui';
import { ButtonIcon, ButtonSize, ButtonText } from '@prisjakt/ui/button';
import { TextButton, TonalButton } from '@prisjakt/ui/button/variants';
import { IconClose } from '@prisjakt/ui/icon';
import {
  FilterDropdown,
  Option,
  OptionTypeEnum,
} from 'src/components/filter-dropdown';
import FilterToggleButton from '../offer-list-filters/components/filter-toggle-button';
import { IncludeShippingFilter } from '../offer-list-filters/components/include-shipping-filter';
import { OfferListFilters } from '../offer-list-filters';

import { getSortOptions } from '../utils';

import {
  FilterType,
  FilterUIPlacementEnum,
  IFilter,
} from 'src/components/price-list/types';
import { getApplicableFilters } from './utils/filter-filters';
import { Price } from 'src/types';

import { Header, HeaderButtons, HeaderFlex } from '../styles';
import { ScrollingFiltersList, ScrollingFiltersListWrapper } from './styles';

type PriceListHeaderProps = {
  onSort: (option: Option) => void;
  sorting: OptionTypeEnum;
  allOffers: Price[];
  allOffersWithActiveAdditives: Price[];
};

const PriceListHeader = ({
  allOffers,
  allOffersWithActiveAdditives,
  onSort,
  sorting,
}: PriceListHeaderProps) => {
  const { t } = useAppContext();
  const {
    getFilterByPlacement,
    getTranslatedLabel,
    toggleFilter,
    resetFiltersByPlacement,
  } = useOfferListFilterContext();
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<IFilter[]>();

  const filtersByPlacement = getFilterByPlacement(
    FilterUIPlacementEnum.FILTER_MENU
  );

  const sortOptions = getSortOptions(t);

  const getCurrentSorting = () => {
    return sortOptions.find(sort => sort.type === sorting) || sortOptions[0];
  };

  useEffect(() => {
    initializeFilters();
  }, [allOffers, allOffersWithActiveAdditives]);

  const initializeFilters = () => {
    const updatedFilters = getApplicableFilters(
      filtersByPlacement,
      allOffers,
      allOffersWithActiveAdditives
    );
    setFilters(updatedFilters);
  };

  const closeHandler = () => setShowFilters(false);
  const toggleHandler = () => setShowFilters(!showFilters);

  const removeFilter = (filterType: FilterType) => {
    toggleFilter(filterType);
  };

  const getActiveFilters =
    filtersByPlacement?.filter(filter => {
      const isNotHidden = !!filter.hidden;
      const activeAdditive = !filter.active && filter.additive;
      const active = filter.active && !filter.additive;

      return isNotHidden && (active || activeAdditive);
    }) || [];

  const activeFilterTicks = () => {
    if (getActiveFilters?.length <= 0) {
      return null;
    }

    const activeFilters = getActiveFilters.map(filter => {
      const label = getTranslatedLabel(t, filter.type);
      let prefix = '';

      if (filter.additive) {
        prefix = t('filter', 'prefix.add');
      }
      return (
        <TonalButton
          size={ButtonSize.SMALL}
          key={label}
          onClick={() => removeFilter(filter.type)}
        >
          <ButtonText> {`${prefix}${label}`}</ButtonText>
          <ButtonIcon>
            <IconClose />
          </ButtonIcon>
        </TonalButton>
      );
    });

    let clearFilterButton;

    if (activeFilters.length >= 2) {
      clearFilterButton = (
        <TextButton
          size={ButtonSize.SMALL}
          onClick={() =>
            resetFiltersByPlacement(FilterUIPlacementEnum.FILTER_MENU)
          }
        >
          {t('filter', 'button.clear-filter')}
        </TextButton>
      );
    }

    return (
      <ScrollingFiltersList>
        <ScrollingFiltersListWrapper>
          {activeFilters}
          {clearFilterButton}
        </ScrollingFiltersListWrapper>
      </ScrollingFiltersList>
    );
  };

  return (
    <Header data-test="PricePanelHeader">
      <HeaderFlex>
        <HeaderButtons>
          <FilterToggleButton
            showFilters={showFilters}
            toggleHandler={toggleHandler}
            activeFilters={getActiveFilters.length}
          />
          <IncludeShippingFilter />
        </HeaderButtons>
        <FilterDropdown
          alignment={Direction.Right}
          defaultSelected={getCurrentSorting()}
          options={sortOptions}
          onFilterChange={option => onSort(option)}
        />
      </HeaderFlex>
      {activeFilterTicks()}
      <OfferListFilters
        filters={filters || []}
        onFilterChanged={(filterType: FilterType) => toggleFilter(filterType)}
        visible={showFilters}
        closeHandler={closeHandler}
        onSortChange={option => onSort(option)}
        currentSorting={getCurrentSorting()}
      />
    </Header>
  );
};

export default PriceListHeader;
